// Which backend this bundle writes to, and how loudly the UI has to say so.
//
// The environment is the API host, not the page host: a dev server on localhost
// pointed at the production API mutates production data while looking exactly
// like a dev session. That combination is the one that causes the mistakes this
// exists to prevent, so it gets the loudest cue rather than none.

export type ApiEnvironment = "production" | "development" | "unknown"

// A Map rather than an object literal: a hostname such as "constructor" must
// not resolve through Object.prototype.
const API_ENVIRONMENT_BY_HOST: ReadonlyMap<string, ApiEnvironment> = new Map([
  ["api.beringlab.com", "production"],
  ["dev.api.beringlab.com", "development"],
])

export const apiHostOf = (baseUrl: string | undefined): string | null => {
  if (!baseUrl) return null
  try {
    return new URL(baseUrl).hostname
  } catch {
    return null
  }
}

export const resolveApiEnvironment = (
  baseUrl: string | undefined
): ApiEnvironment => {
  const host = apiHostOf(baseUrl)
  return (host && API_ENVIRONMENT_BY_HOST.get(host)) || "unknown"
}

export interface EnvironmentCue {
  /** Short tag shown in the badge and the frame label. */
  label: string
  /** The API host, or why there is none. */
  detail: string
  /** One character for the favicon tile. */
  glyph: string
  tone: "danger" | "caution"
  /**
   * Whether every page carries the cue. A deployed build carries it only when
   * its API is positively the dev one: anything else may be in front of
   * customers, and the back office badge covers staff there.
   */
  pervasive: boolean
}

export const describeEnvironment = ({
  apiBaseUrl,
  isDevServer,
}: {
  apiBaseUrl: string | undefined
  isDevServer: boolean
}): EnvironmentCue => {
  const environment = resolveApiEnvironment(apiBaseUrl)
  const detail = apiHostOf(apiBaseUrl) ?? "VITE_BASE_API_URL 미설정"
  const local = isDevServer ? "LOCAL · " : ""

  switch (environment) {
    case "production":
      return isDevServer
        ? {
            label: "LOCAL → PROD API",
            detail,
            glyph: "P",
            tone: "danger",
            pervasive: true,
          }
        : { label: "PROD", detail, glyph: "P", tone: "danger", pervasive: false }
    case "development":
      return {
        label: `${local}DEV`,
        detail,
        glyph: "D",
        tone: "caution",
        pervasive: true,
      }
    case "unknown":
      return {
        label: `${local}API 미확인`,
        detail,
        glyph: "?",
        tone: "caution",
        // Not on a deployed build: a production API host this table does not
        // know yet (a migration, a new region) would frame the customer site.
        pervasive: isDevServer,
      }
  }
}

export const environmentCue = describeEnvironment({
  apiBaseUrl: import.meta.env.VITE_BASE_API_URL,
  isDevServer: import.meta.env.DEV,
})

// Fixed values rather than theme tokens: the dark theme lightens red-600, which
// leaves white text on it unreadable, and the favicon cannot read CSS anyway.
export const ENVIRONMENT_TONE_COLORS = {
  danger: { fill: "#DC2626", ink: "#FFFFFF" },
  caution: { fill: "#F59E0B", ink: "#1C1917" },
} as const satisfies Record<EnvironmentCue["tone"], unknown>

export const environmentFaviconHref = ({ glyph, tone }: EnvironmentCue) => {
  const { fill, ink } = ENVIRONMENT_TONE_COLORS[tone]
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="7" fill="${fill}"/><text x="16" y="23.5" text-anchor="middle" font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif" font-size="22" font-weight="700" fill="${ink}">${glyph}</text></svg>`
  )}`
}

/**
 * Tabs are where dev and prod sit side by side, and a title prefix would have to
 * chase every `document.title` assignment, so the favicon carries the cue.
 *
 * Call before the first render: the assistant completion monitor captures the
 * icon it finds as the one to restore after its blue dot, and it has to capture
 * this one.
 */
export const applyEnvironmentFavicon = (cue: EnvironmentCue) => {
  if (!cue.pervasive) return
  const icon = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
  if (!icon) return
  icon.setAttribute("href", environmentFaviconHref(cue))
  icon.setAttribute("type", "image/svg+xml")
}
